import * as Three from 'three';
import toast from 'react-hot-toast';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
  addImage,
  addVideo,
  addAudio,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { UpdateMaterial } from './materialFunction';
import { beginSuppressedMeshAddHistory } from '../../utils/gridHistoryDiscrete';
import { markFilePivotMode } from '../../pages/Training/utils/gridpageUtils';

/**
 * Handles adding library 3D assets to the scene
 * @param {Object} asset - Library asset object with _id, name/title, url, category/type
 * @param {Object} sceneObjects - { scene, transformControls, setPolyCount }
 */
export async function handleLibraryAssetImport(asset, sceneObjects) {
  const { scene, transformControls, setPolyCount } = sceneObjects;

  // Normalise name — library API may use `title`, Sketchfab uses `name`
  const assetName = asset?.name || asset?.title || 'Asset';

  // Prefer streamUrl (compressed GLB from CDN) over url — it loads faster and
  // is the version that actually works. Fall back to url if streamUrl is absent.
  const resolvedUrl = asset?.streamUrl || asset?.url || asset?.asset_file_path || null;

  if (!asset || !resolvedUrl) {
    toast.error('Invalid library asset — missing URL');
    return;
  }

  if (!scene) {
    toast.error('Scene not ready, please try again');
    return;
  }

  store.dispatch(setLoadingMessage(true));
  store.dispatch(selected_object(null));

  const assetType = getAssetType({ ...asset, url: resolvedUrl });

  // Attach normalised name and resolved URL so all sub-functions use them
  const normalisedAsset = { ...asset, name: assetName, url: resolvedUrl };

  try {
    switch (assetType) {
      case '3d':
        await loadLibraryMesh(normalisedAsset, scene, setPolyCount);
        break;
      case 'image':
        await loadLibraryImage(normalisedAsset, scene);
        break;
      case 'video':
        await loadLibraryVideo(normalisedAsset, scene);
        break;
      case 'audio':
        await loadLibraryAudio(normalisedAsset);
        break;
      default:
        toast.error(`Unsupported asset type: ${assetType}`);
        return;
    }
    toast.success(`${assetName} added to scene successfully`);
  } catch (error) {
    console.error('Error loading library asset:', error);
    toast.error(`Failed to load ${assetName}`);
  } finally {
    store.dispatch(setLoadingMessage(false));
  }
}

/**
 * Determine asset type from API category/type or URL extension
 */
function getAssetType(asset) {
  // Map category or type field from the API to a known asset type
  const categoryRaw = (asset.category || asset.type || '').toUpperCase();
  if (categoryRaw) {
    if (['3D', '3D_ASSET', 'MODEL', 'MESH', 'SCENE', 'TEMPLATES', 'SCENES', 'VECTORS', 'SPRITES'].includes(categoryRaw)) return '3d';
    if (['IMAGE', 'IMAGES', 'PHOTO', 'TEXTURE'].includes(categoryRaw)) return 'image';
    if (['VIDEO', 'VIDEOS'].includes(categoryRaw)) return 'video';
    if (['AUDIO', 'SOUND', 'MUSIC'].includes(categoryRaw)) return 'audio';
  }

  // Fallback: check URL extension
  const url = asset.url || '';
  const ext = url.split('.').pop().toLowerCase();

  const typeMap = {
    glb: '3d',
    gltf: '3d',
    fbx: '3d',
    obj: '3d',
    ply: '3d',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    gif: 'image',
    mp4: 'video',
    webm: 'video',
    avi: 'video',
    mov: 'video',
    mp3: 'audio',
    wav: 'audio',
    ogg: 'audio',
    aac: 'audio',
  };

  return typeMap[ext] || '3d'; // Default to 3D
}

/**
 * Load library 3D mesh and add to scene
 */
async function loadLibraryMesh(asset, scene, setPolyCount) {
  const meshAddHistory = beginSuppressedMeshAddHistory(scene);

  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    loader.setDRACOLoader(dracoLoader);

    const tempId = `library-${asset._id}-${Date.now()}`;

    loader.load(
      asset.url,
      (gltf) => {
        try {
        const model = gltf.scene;

        // ===============================
        // ANIMATIONS
        // ===============================
        if (Array.isArray(gltf.animations) && gltf.animations.length > 0) {
          const mixer = new Three.AnimationMixer(model);
          model.userData = model.userData || {};
          model.userData.mixer = mixer;
          model.userData.animationActions = [];
          model.userData.animationClips = gltf.animations;

          gltf.animations.forEach((clip) => {
            const action = mixer.clipAction(clip);
            action.reset();
            action.setLoop(Three.LoopRepeat);
            model.userData.animationActions.push(action);
          });
        }

        // ===============================
        // CREATE PIVOT GROUP
        // ===============================
        const pivot = new Three.Group();
        pivot.name = tempId;
        pivot._id = tempId;
        pivot.url = asset.url;
        pivot.deleteType = 'mesh';
        pivot.objType = '3d';
        pivot.source = 'library'; // Mark as library asset
        pivot.libraryAssetId = asset._id;

        // Keep the GLB file origin as the selection/transform pivot.
        pivot.add(model);
        markFilePivotMode(pivot);

        // ===============================
        // MATERIAL & SHADOWS
        // ===============================
        model.traverse((child) => {
          if (!child.isMesh) return;

          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(preserveMaterial);
            } else {
              preserveMaterial(child.material);
            }
          }

          child.castShadow = true;
          child.receiveShadow = true;
        });

        // ===============================
        // METADATA
        // ===============================
        pivot.file_name = asset.name;
        pivot.temp_file_name = tempId;
        pivot.originalFile = null;
        pivot.localFile = null;

        // Seed an empty material_settings on the pivot's userData so that
        // UpdateMaterial has a base to merge into, and so publish can always
        // read pivot.userData.material_settings — even before the user edits anything.
        pivot.userData = pivot.userData || {};
        pivot.userData.material_settings = {
          renderingMode: 'Opaque',
          materialType: 'MeshStandardMaterial',
          type: 'MeshStandardMaterial',
          shader: 'Standard',
          albedo: '#ffffff',
          alpha: 1,
          opacity: 1,
          side: 'Double',
          blending: 'Normal',
          emission: '',
          emissionType: false,
          metallic: { metalness: 0.5, roughness: 0.5, map: '' },
          metalicType: false,
          normal_map: 'Unselected',
          transparency: false,
          textureImage: '',
          aoMapIntensity: 1,
          mirror: false,
          castShadow: true,
          receiveShadow: true,
          NormalMap: '',
        };

        // ===============================
        // ADD TO SCENE & REDUX
        // ===============================
        scene.add(pivot);
        setPolyCount?.((c) => c + 1);

        // Create a plain serializable object for Redux (not the live Three.js object)
        const meshReduxRecord = {
          _id: tempId,
          name: tempId,
          temp_file_name: tempId,
          file_name: asset.name,
          url: asset.url,
          // asset_file_path ensures .glb detection works in AssetList even before
          // the server record (which carries asset_file_path) is available after publish.
          asset_file_path: asset.url,
          source: 'library',
          libraryAssetId: asset._id,
          glb_type: 'normal',
          deleteType: 'mesh',
          objType: '3d',
          position: { x: worldCenter.x, y: worldCenter.y, z: worldCenter.z },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 },
          parent: null, // Top-level by default
          // Seed material_settings so publish can read it even before the user
          // edits anything. Without this the first-publish fallback finds an
          // empty record and sends empty material_settings to the API, causing
          // material changes to vanish after publish.
          material_settings: { ...pivot.userData.material_settings },
        };

        store.dispatch(setPushIntersectedObject(pivot));
        store.dispatch(add_new_assets({ type: 'mesh', data: meshReduxRecord }));
        store.dispatch(set_is_glb_image_editoropen(true));
        store.dispatch(addModel({ url: asset.url, name: asset.name }));
        store.dispatch(addSceneMesh(meshReduxRecord));

        selectObjectInScene(tempId);
        meshAddHistory.finish('add mesh');

        // Notify AppLeftSection that the pivot is live in the scene so it
        // re-runs GLB child extraction immediately (scene.children mutations
        // are invisible to React without this signal).
        try {
          window.dispatchEvent(
            new CustomEvent('glbMeshLoaded', {
              detail: { meshId: tempId, pivotName: pivot.name },
            })
          );
        } catch (_) {}

        resolve(pivot);
        } catch (error) {
          meshAddHistory.cancel();
          reject(error);
        }
      },
      undefined,
      (error) => {
        console.error('Error loading library asset:', error);
        meshAddHistory.cancel();
        reject(error);
      }
    );
  });
}

/**
 * Load library image and add to scene as plane
 */
async function loadLibraryImage(asset, scene) {
  return new Promise((resolve, reject) => {
    const textureLoader = new Three.TextureLoader();
    const tempId = `library-image-${asset._id}-${Date.now()}`;

    textureLoader.load(
      asset.url,
      (texture) => {
        // Create plane geometry for image
        const geometry = new Three.PlaneGeometry(10, 10);
        const material = new Three.MeshBasicMaterial({ map: texture });
        const mesh = new Three.Mesh(geometry, material);

        mesh.name = tempId;
        mesh._id = tempId;
        mesh.url = asset.url;
        mesh.deleteType = 'image';
        mesh.objType = 'image';
        mesh.source = 'library';
        mesh.libraryAssetId = asset._id;
        mesh.file_name = asset.name;
        mesh.temp_file_name = tempId;

        scene.add(mesh);

        // Create a plain serializable object for Redux (not the live Three.js mesh)
        const imageReduxRecord = {
          _id: tempId,
          name: tempId,
          temp_file_name: tempId,
          file_name: asset.name,
          url: asset.url,
          source: 'library',
          libraryAssetId: asset._id,
          deleteType: 'image',
          objType: 'image',
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 },
          parent: null,
        };

        store.dispatch(setPushIntersectedObject(mesh));
        store.dispatch(add_new_assets({ type: 'image', data: imageReduxRecord }));
        store.dispatch(addImage({ url: asset.url, name: asset.name }));

        selectObjectInScene(tempId);
        resolve(mesh);
      },
      undefined,
      reject
    );
  });
}

/**
 * Load library video and add to scene as plane
 */
async function loadLibraryVideo(asset, scene) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.src = asset.url;
    video.crossOrigin = 'Anonymous';
    video.loop = true;
    video.muted = true;
    video.play().catch(() => console.warn('Video autoplay prevented'));

    const videoTexture = new Three.VideoTexture(video);
    const tempId = `library-video-${asset._id}-${Date.now()}`;

    // Create plane for video
    const geometry = new Three.PlaneGeometry(10, 10);
    const material = new Three.MeshBasicMaterial({ map: videoTexture });
    const mesh = new Three.Mesh(geometry, material);

    mesh.name = tempId;
    mesh._id = tempId;
    mesh.url = asset.url;
    mesh.deleteType = 'video';
    mesh.objType = 'video';
    mesh.source = 'library';
    mesh.libraryAssetId = asset._id;
    mesh.file_name = asset.name;
    mesh.temp_file_name = tempId;
    mesh.userData = { video, texture: videoTexture };

    scene.add(mesh);

    // Create a plain serializable object for Redux
    const videoReduxRecord = {
      _id: tempId,
      name: tempId,
      temp_file_name: tempId,
      file_name: asset.name,
      url: asset.url,
      source: 'library',
      libraryAssetId: asset._id,
      deleteType: 'video',
      objType: 'video',
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      parent: null,
      userData: { video },
    };

    store.dispatch(setPushIntersectedObject(mesh));
    store.dispatch(add_new_assets({ type: 'video', data: videoReduxRecord }));
    store.dispatch(addVideo({ url: asset.url, name: asset.name }));

    selectObjectInScene(tempId);
    resolve(mesh);
  });
}

/**
 * Load library audio
 */
async function loadLibraryAudio(asset) {
  return new Promise((resolve, reject) => {
    const audioLoader = new Three.AudioLoader();
    const tempId = `library-audio-${asset._id}-${Date.now()}`;

    audioLoader.load(
      asset.url,
      (audioBuffer) => {
        const audio = new Three.Audio(new Three.AudioListener());
        audio.setBuffer(audioBuffer);
        audio.name = tempId;
        audio._id = tempId;
        audio.deleteType = 'audio';
        audio.objType = 'audio';
        audio.source = 'library';
        audio.libraryAssetId = asset._id;
        audio.file_name = asset.name;
        audio.temp_file_name = tempId;

        // Create a plain serializable object for Redux
        const audioReduxRecord = {
          _id: tempId,
          name: tempId,
          temp_file_name: tempId,
          file_name: asset.name,
          url: asset.url,
          source: 'library',
          libraryAssetId: asset._id,
          deleteType: 'audio',
          objType: 'audio',
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 },
          parent: null,
        };

        store.dispatch(setPushIntersectedObject(audio));
        store.dispatch(add_new_assets({ type: 'audio', data: audioReduxRecord }));
        store.dispatch(addAudio({ url: asset.url, name: asset.name }));

        store.dispatch(selected_object(audio));
        resolve(audio);
      },
      undefined,
      reject
    );
  });
}

/**
 * Compute world-space geometric center (fast)
 */
function computeWorldPolyCenter(object) {
  const box = new Three.Box3().setFromObject(object);
  if (box.isEmpty()) return null;
  const center = new Three.Vector3();
  box.getCenter(center);
  return center;
}

/**
 * Preserve and setup material properties
 */
function preserveMaterial(material) {
  if (!material) return;

  material.needsUpdate = true;

  if (material.map) {
    material.map.needsUpdate = true;
    material.map.flipY = false;
  }
  if (material.normalMap) {
    material.normalMap.needsUpdate = true;
    material.normalMap.flipY = false;
  }
  if (material.roughnessMap) {
    material.roughnessMap.needsUpdate = true;
    material.roughnessMap.flipY = false;
  }
  if (material.metalnessMap) {
    material.metalnessMap.needsUpdate = true;
    material.metalnessMap.flipY = false;
  }
  if (material.aoMap) {
    material.aoMap.needsUpdate = true;
    material.aoMap.flipY = false;
  }

  // Save original material properties
  material.userData.originalMaterialProps = {
    type: material.type,
    color: material.color ? material.color.getHex() : undefined,
    metalness: material.metalness,
    roughness: material.roughness,
    map: material.map || null,
    normalMap: material.normalMap || null,
    roughnessMap: material.roughnessMap || null,
    metalnessMap: material.metalnessMap || null,
    aoMap: material.aoMap || null,
    emissive: material.emissive ? material.emissive.getHex() : undefined,
    emissiveIntensity: material.emissiveIntensity,
    emissiveMap: material.emissiveMap || null,
    opacity: material.opacity,
    transparent: material.transparent,
    alphaTest: material.alphaTest,
    depthWrite: material.depthWrite,
    blending: material.blending,
    side: material.side,
    wireframe: material.wireframe,
  };

  material.userData.material_settings = {
    albedo: '#00FFFFFF',
    metalicType: false,
    metalness: material.metalness || 0.5,
    roughness: material.roughness || 0.5,
  };
}
